import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Typography } from '@material-tailwind/react';
import {
  IdentificationIcon,
  CheckCircleIcon,
  XCircleIcon,
  FolderIcon,
  CreditCardIcon,
  ClipboardDocumentCheckIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet';

import Layout from '../components/layout/Layout';
import PropertySelector from '../components/PropertySelector';
import { useProperties } from '../contexts/PropertiesContext';
import { trackEvent } from '../utils/analytics';
import PropertyCard from '../components/PropertyCard';
import axiosInstance from '../utils/axiosInstance';
import PaymentModal from '../components/modals/PaymentModal';

// Définition des actions possibles
const ACTION_DEFINITIONS = [
  {
    key: 'quoteAcceptance',
    title: 'Acceptation devis, ordre de mission & CGVU',
    description: 'Consultez et acceptez le devis, l\'ordre de mission et les Conditions Générales de Vente et d\'Utilisation.',
    icon: ClipboardDocumentCheckIcon,
    link: (id) => `/quote/${id}`,
    isDone: (property) => property.status === 'won'
  },
  {
    key: 'bankInfo',
    title: 'Empreinte bancaire (Confirmation RDV)',
    description: 'Enregistrez une empreinte de carte bancaire pour confirmer définitivement votre rendez-vous.',
    icon: CreditCardIcon,
    link: (id) => `/bank-imprint/${id}`,
    isDone: (property) => !property.actionsNeeded?.bankImprintNeeded,
  },
  {
    key: 'appointment',
    title: 'Préparer mon RDV',
    description: 'Consultez les informations et préparez votre rendez-vous avec notre technicien.',
    icon: CalendarIcon,
    link: (id) => `/appointment/${id}`,
    isDone: (property) => !property.actionsNeeded?.appointment
  },
  {
    key: 'customerDocument',
    title: 'Fourniture de documents',
    description: 'Transmettez les documents requis : titre de propriété, factures de travaux/consommation, rapports d\'entretien...',
    icon: FolderIcon,
    link: (id) => `/documents/${id}`,
    isDone: (property) => !property.actionsNeeded?.customerDocument && 
                         !property.actionsNeeded?.titleOfPropertyMissing && 
                         !property.actionsNeeded?.consumptionInvoiceMissing && 
                         !property.actionsNeeded?.maintenanceReportMissing &&
                         !property.actionsNeeded?.energyRenovationMissing
  },
  {
    key: 'dpe', 
    title: 'Formulaire de consentement DPE',
    description: 'Confirmez votre choix pour la transmission des données relatives au DPE à l\'ADEME.',
    icon: IdentificationIcon,
    link: (id) => `/dpe/${id}`,
    isDone: (property) => !property.actionsNeeded?.dpe
  },
  {
    key: 'fiscalId',
    title: 'Numéro fiscal',
    description: 'Renseignez l\'identifiant fiscal unique de votre local, nécessaire pour le DPE ou l\'Audit.',
    icon: IdentificationIcon,
    link: (id) => `/fiscal-id/${id}`,
    isDone: (property) => !property.actionsNeeded?.fiscalId
  },
  {
    key: 'payment',
    title: 'Payer ma facture',
    description: 'Réglez votre facture pour accéder à vos documents.',
    icon: CreditCardIcon,
    isDone: (property) => property.isPaided === true,
    isDisabled: (property) => property.isPayable === false,
  },
];

const ActionsSkeleton = () => {
  return (
    <Card className="w-full mb-6 shadow-md animate-pulse">
      <div className="p-6 space-y-6">
        <div className="h-8 bg-gray-200 rounded-lg w-1/3 mb-6"></div>
        {/* Skeleton pour la liste de propriétés ou le workflow */}
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <Card key={index} className="p-4 bg-gray-50 border border-gray-200">
              <div className="flex justify-between items-center">
                <div className="space-y-2 w-3/4">
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </div>
                <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default function Actions() {
  const { id: propertyIdFromUrl } = useParams();
  const navigate = useNavigate();
  const { properties, loading, error } = useProperties();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [sortedProperties, setSortedProperties] = useState([]);
  const [hasAnyActionsOverall, setHasAnyActionsOverall] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  useEffect(() => {
    if (!loading && properties) {
      // Filtrer et trier les propriétés
      const filteredAndSorted = [...properties]
        .filter(p => {
          // Exclure si l'unique diagnostic est ERP
          const isOnlyERP = Array.isArray(p.diagnostics) && p.diagnostics.length === 1 && p.diagnostics[0] === 'ERP';
          return !isOnlyERP;
        })
        .sort((a, b) => b.id - a.id);
      
      setSortedProperties(filteredAndSorted);

      // Vérifier si au moins une propriété a des actions
      const anyActions = filteredAndSorted.some(p => 
        Object.values(p.actionsNeeded || {}).some(actionNeeded => actionNeeded === true)
      );
      setHasAnyActionsOverall(anyActions);

      if (propertyIdFromUrl) {
        // Trouver la propriété dans la liste triée
        const prop = filteredAndSorted.find(p => p.id.toString() === propertyIdFromUrl);
        setSelectedProperty(prop);
      } else if (filteredAndSorted.length === 1) {
        // Si une seule propriété au total, la sélectionner (pas besoin d'action requise)
        setSelectedProperty(filteredAndSorted[0]);
        navigate(`/actions/${filteredAndSorted[0].id}`, { replace: true });
      } else {
        // Si pas d'ID dans l'URL et plusieurs propriétés, ne rien présélectionner
        setSelectedProperty(null);
      }
    }
  }, [properties, loading, propertyIdFromUrl, navigate]);

  // Effet pour vider le cache côté serveur lorsque selectedProperty change
  useEffect(() => {
    if (selectedProperty && selectedProperty.id) {
      const clearCache = async () => {
        try {
          await axiosInstance.post(`/properties/${selectedProperty.id}/clear-cache`);
          console.log(`Cache cleared for property ${selectedProperty.id}`);
        } catch (error) {
          console.error("Failed to clear cache:", error);
          // Gérer l'erreur si nécessaire (par exemple, afficher un message)
        }
      };
      clearCache();
    }
  }, [selectedProperty]); // Déclencher quand selectedProperty change

  const handlePropertySelect = (property) => {
    trackEvent('actions_select_property', { propertyId: property.id });
    setSelectedProperty(property);
    navigate(`/actions/${property.id}`);
  };

  // Handler pour le PropertySelector
  const handlePropertySelectFromSelector = (newPropertyId) => {
    if (newPropertyId !== selectedProperty?.id.toString()) {
        trackEvent('actions_change_property_selector', { propertyId: newPropertyId });
        navigate(`/actions/${newPropertyId}`);
    }
  };

  // Handlers pour le modal de paiement
  const handleOpenPaymentModal = () => {
    trackEvent('actions_open_payment_modal', { propertyId: selectedProperty?.id });
    setIsPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    trackEvent('actions_close_payment_modal', { propertyId: selectedProperty?.id });
    setIsPaymentModalOpen(false);
  };

  const renderActionWorkflow = (property) => {
    if (!property) return null;

    trackEvent('actions_view_workflow', { propertyId: property.id });

    return (
      <>
        <Typography variant="h5" color="blue-gray" className="mb-4">
          Actions à mener pour le dossier #{property.id}
        </Typography>

        <PropertyCard
          property={property}
          variant="informative"
          className="mb-6"
        />

        <div className="space-y-4">
          {/* 1. Filtrer les actions à afficher */}
          {(() => {
            const hasDPEOrAudit = property.diagnostics?.some(diag => 
              diag === 'DPE' || diag === 'Audit énergétique réglementaire'
            );

            const renderableActions = ACTION_DEFINITIONS.filter(action => {
              if (action.isPlaceholder) return false;
              if (['dpe', 'fiscalId'].includes(action.key) && !hasDPEOrAudit) return false;
              return true;
            });

            return renderableActions.map((action, index, arr) => {
              const isActionDone = action.isDone(property);
              const isDisabled = action.isDisabled ? action.isDisabled(property) : false;
              const IconComp = action.icon;

              const cardClasses = `
                p-4 border flex flex-row items-center gap-4 transition-all duration-200 w-full 
                ${isActionDone 
                  ? 'border-green-200 bg-green-50' 
                  : isDisabled 
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed' 
                    : 'border-orange-200 bg-orange-50 hover:bg-orange-100 hover:shadow-md cursor-pointer'
                }
              `;

              const iconContainerClasses = `flex-shrink-0 p-2 rounded-full 
                ${isActionDone ? 'bg-green-100' : isDisabled ? 'bg-gray-100' : 'bg-orange-100'}
              `;

              const iconClasses = `h-6 w-6 
                ${isActionDone ? 'text-green-600' : isDisabled ? 'text-gray-400' : 'text-orange-600'}
              `;
              
              const statusIcon = isActionDone 
                ? <CheckCircleIcon className="h-6 w-6 text-green-500" /> 
                : isDisabled 
                  ? null
                  : <XCircleIcon className="h-6 w-6 text-orange-500" />;

              const handleCardClick = () => {
                if (isActionDone || isDisabled) return;
                
                if (action.key === 'payment') {
                  handleOpenPaymentModal();
                } else if (action.link) {
                  navigate(action.link(property.id));
                }
              };

              return (
                <div key={index} className="relative sm:pl-12 pb-2">
                  {/* Numéro et Ligne de liaison */} 
                  <div className="absolute left-0 top-4 h-full hidden sm:flex"> 
                    <div 
                       className={`z-10 w-8 h-8 rounded-full flex items-center justify-center font-bold text-white mt-2 
                         ${isActionDone ? 'bg-green-500' : isDisabled ? 'bg-gray-400' : 'bg-orange-500'}
                       `}
                     >
                       {index + 1}
                    </div>
                    {/* Ligne verticale (sauf pour le dernier) */} 
                    {index < arr.length - 1 && (
                      <div 
                        className="absolute left-3.5 h-full border-l-2 border-gray-300"
                        style={{ top: '2rem', /* Commence sous le cercle h-8 */ bottom: '0' }} /* S'étend jusqu'en bas */
                      ></div>
                    )}
                  </div>

                  {/* Carte d'action */}
                  <Card
                    className={cardClasses}
                    onClick={handleCardClick}
                  >
                    <div className={iconContainerClasses}>
                      <IconComp
                        className={iconClasses}
                      />
                    </div>
                    <div className="flex-grow">
                      <Typography variant="h6" color={isDisabled ? "gray" : "blue-gray"}>{action.title}</Typography>
                      <Typography variant="small" color="gray">{action.description}</Typography>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                       {statusIcon} 
                    </div>
                  </Card>
                </div>
              );
            });
          })()}
        </div>
      </>
    );
  };

  const renderPropertyList = () => {
    trackEvent('actions_view_property_list');
    return (
      <div className="space-y-4">
        <Typography color="blue-gray" className="mb-4">
          {sortedProperties.length > 1 ? 'Veuillez sélectionner le dossier pour lequel vous souhaitez voir les actions à mener :' : (sortedProperties.length === 1 ? 'Votre dossier :' : 'Aucun dossier compatible trouvé.')}
        </Typography>
        {sortedProperties.length > 0 ? sortedProperties.map((property) => {
          return (
            <PropertyCard
              key={property.id}
              property={property}
              variant="actionable"
              onClick={() => handlePropertySelect(property)}
              showActionsRequired={true}
            />
          );
        }) : (
          <Typography color="gray">Aucun dossier nécessitant des actions ou compatible n'a été trouvé.</Typography>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <ActionsSkeleton />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Typography color="red" className="text-center">
          {error}
        </Typography>
      </Layout>
    );
  }

  if (!loading && !hasAnyActionsOverall) {
    return (
      <Layout>
         <Helmet>
          <title>Actions à mener - Espace client Dimo Diagnostic</title>
          <meta name="description" content="Consultez les actions requises pour vos dossiers." />
        </Helmet>
        <Card className="w-full mb-6 shadow-md">
           <div className="p-6 text-center">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
             <Typography variant="h5" color="blue-gray" className="mb-2">
               Tout est en ordre !
             </Typography>
             <Typography color="gray">
               Aucune action n'est requise de votre part pour le moment.
             </Typography>
           </div>
         </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Actions à mener - Espace client Dimo Diagnostic</title>
        <meta name="description" content="Consultez les actions requises pour vos dossiers." />
      </Helmet>
      <Card className="w-full mb-6 shadow-md">
        <div className="p-6">
          {/* N'afficher le titre que si aucune propriété n'est sélectionnée */} 
          {!selectedProperty && (
            <Typography variant="h4" color="blue-gray" className="mb-6">
              Actions à mener
            </Typography>
          )}

          {/* Afficher le sélecteur ici si une propriété est sélectionnée et s'il y en a plusieurs */} 
          {selectedProperty && sortedProperties.length > 1 && (
            <div className="mb-6">
              <PropertySelector
                selectedProperty={selectedProperty.id.toString()} // Doit être une chaîne
                setSelectedProperty={handlePropertySelectFromSelector}
                properties={sortedProperties}
                isLoading={loading}
                displayContext="actions"
              />
            </div>
          )}

          {selectedProperty
            ? renderActionWorkflow(selectedProperty)
            : renderPropertyList()}
        </div>
      </Card>

      {/* Modal de paiement */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={handleClosePaymentModal}
        dealId={selectedProperty ? selectedProperty.id : null}
        paymentLink={selectedProperty ? selectedProperty.paymentLink : null}
        totalAmount={selectedProperty ? selectedProperty.amount : null}
      />
    </Layout>
  );
} 